import * as React from 'react';
import { getFirstFocusable } from '@fluentui/react/lib/Utilities';
import { jumpToRegion } from 'owa-locstrings/lib/strings/jumptoregion.locstring.json';
import { observer } from 'mobx-react-lite';
import { useMergedRefs } from 'owa-react-hooks/lib/useMergedRefs';
import loc from 'owa-localize';

export default observer(
    function SkipLinkRegionToFirstFocusableChild(
        {
            role,
            regionNameResourceId,
            children,
            ...divProps
        }: {
            // See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques#Landmark_roles
            role: 'navigation' | 'main' | 'complementary' | 'search' | 'region';
            regionNameResourceId?: string;
            children: React.ReactNode | React.ReactNode[];
        } & Omit<React.HTMLAttributes<HTMLDivElement>, 'role' | 'aria-label'>,
        ref: React.Ref<HTMLDivElement>
    ) {
        const skipLinkRef = React.useRef<HTMLDivElement>();
        const regionName = loc(regionNameResourceId);
        const skipLinkName = loc(jumpToRegion, regionName);

        React.useEffect(() => {
            const e = skipLinkRef.current;
            if (e) {
                getFirstFocusable(e, e, true)?.setAttribute('data-skip-link-name', skipLinkName);
            }
        }, [skipLinkRef, skipLinkName]);

        return (
            <div
                {...divProps}
                role={role}
                aria-label={regionName}
                ref={useMergedRefs(ref, skipLinkRef)}>
                {children}
            </div>
        );
    },
    { forwardRef: true }
);
